import { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, User, Trash2, CheckCircle2 } from 'lucide-react';

const SKIP_REASONS = [
  { value: 'no_one_on_site', label: 'No one on site' },
  { value: 'customer_refused', label: 'Customer refused' },
  { value: 'other', label: 'Other' },
];

/**
 * Capture the customer's start-of-day authorisation signature for a haulage job.
 * Optional/skippable with a reason. Persists to POST /api/jobs/{jobId}/start-signoff.
 */
export const StartSignoffDialog = ({ open, onOpenChange, jobId, api, onSaved, timing }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [skipNotes, setSkipNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setHasSignature(false);
      setCustomerName('');
      setConfirmed(false);
      setSkipped(false);
      setSkipReason('');
      setSkipNotes('');
    }
  }, [open]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDrawing = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };
  const stopDrawing = () => { drawing.current = false; };
  const clearSignature = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = async () => {
    if (skipped) {
      if (!skipReason) { toast.error('Please select a reason for skipping'); return; }
    } else {
      if (!customerName.trim()) { toast.error('Please enter the customer name'); return; }
      if (!confirmed) { toast.error('Please tick the confirmation'); return; }
    }
    setSaving(true);
    try {
      const payload = skipped
        ? { skipped: true, skip_reason: skipReason, skip_notes: skipNotes.trim() || null }
        : {
            skipped: false,
            customer_name: customerName.trim(),
            signature: hasSignature ? canvasRef.current.toDataURL('image/png') : null,
            confirmed: true,
          };
      const res = await api.post(`/api/jobs/${jobId}/start-signoff`, payload);
      onSaved(res.data.start_signoff);
      toast.success('Start-of-day sign-on saved');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sign-on');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md" data-testid="start-signoff-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--m-text)]">{timing === 'anytime' ? 'Customer Authorisation' : 'Customer Sign-On (start of day)'}</DialogTitle>
          <DialogDescription className="text-[var(--m-text-secondary)] text-xs">
            {timing === 'anytime'
              ? 'Capture the customer\'s authorisation for this job. Optional — you can skip with a reason.'
              : 'Capture the customer\'s authorisation before starting the run. Optional — you can skip with a reason.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div
            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${skipped ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[var(--m-bg)] border border-[var(--m-border)]'}`}
            onClick={() => setSkipped(!skipped)}
            data-testid="start-signoff-skip-toggle"
          >
            <Checkbox checked={skipped} className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
            <span className={`text-sm ${skipped ? 'text-amber-400 font-medium' : 'text-[var(--m-text-secondary)]'}`}>Unable to capture signature</span>
          </div>

          {skipped ? (
            <div className="space-y-2">
              {SKIP_REASONS.map((r) => (
                <div
                  key={r.value}
                  onClick={() => setSkipReason(r.value)}
                  className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer ${skipReason === r.value ? 'bg-[#ffd108]/10 border border-[#ffd108]/40' : 'bg-[var(--m-bg)] border border-[var(--m-border)]'}`}
                  data-testid={`start-signoff-skip-reason-${r.value}`}
                >
                  <div className={`h-4 w-4 rounded-full border flex items-center justify-center ${skipReason === r.value ? 'border-[#ffd108]' : 'border-[var(--m-border-light)]'}`}>
                    {skipReason === r.value && <div className="h-2 w-2 rounded-full bg-[#ffd108]" />}
                  </div>
                  <span className={`text-sm ${skipReason === r.value ? 'text-[var(--m-text)]' : 'text-[var(--m-text-secondary)]'}`}>{r.label}</span>
                </div>
              ))}
              <textarea
                value={skipNotes}
                onChange={(e) => setSkipNotes(e.target.value)}
                placeholder="Additional notes (optional)"
                rows={2}
                className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md px-3 py-2 text-[var(--m-text)] text-sm"
                data-testid="start-signoff-skip-notes"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Customer Name</label>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[var(--m-text-muted)]" />
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Name of person signing"
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    data-testid="start-signoff-name"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-[var(--m-text-secondary)]">Signature</label>
                  {hasSignature && (
                    <Button variant="ghost" size="sm" onClick={clearSignature} className="text-red-400 h-6 px-2">
                      <Trash2 className="h-3 w-3 mr-1" /> Clear
                    </Button>
                  )}
                </div>
                <div className="border border-[var(--m-border-light)] rounded-lg overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    width={300}
                    height={150}
                    className="w-full touch-none"
                    style={{ background: '#ffffff' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="start-signoff-canvas"
                  />
                </div>
                <p className="text-[var(--m-text-muted)] text-xs mt-1 text-center">Draw signature above</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-[var(--m-bg)] rounded-lg">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={setConfirmed}
                  className="mt-0.5 data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108]"
                  data-testid="start-signoff-confirm"
                />
                <span className="text-[var(--m-text-body)] text-sm">I authorise the works for today's run.</span>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="bg-[#ffd108] text-black hover:bg-[#e5bd00]" onClick={handleSave} disabled={saving} data-testid="start-signoff-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Save Sign-On</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StartSignoffDialog;
